// Record a Square payment on a quote without double-counting a card payment
// staff already logged by hand (franklycatering #1217). square-reconcile and
// square-webhooks skipped a Square payment only when a row with its id was
// already on the quote, so a row staff logged by hand (square_payment_id NULL)
// was invisible to them and the same money was counted twice. Now both record
// through recordOrClaimSquarePayment, which first claims such a row: same
// quote, a card method, the same amount and a payment_date within
// CLAIM_WINDOW_DAYS. The claimed row takes the Square id, so every later
// reconcile sweep and webhook retry sees the payment as recorded.
//
// Typed and Deno-free so vitest can exercise it
// (apps/app/src/test/unit/square-payment-claim.test.ts).

export const CLAIM_WINDOW_DAYS = 3;

/** The methods a card payment is logged under (RecordPaymentDialog's CARD_METHODS). */
const HAND_LOGGED_CARD_METHODS = ["credit_card", "square"] as const;

/**
 * The note prefixes square-reconcile and square-webhooks write (#1230). A row
 * with one of them was written by Square sync for a different payment (before
 * #801 added square_payment_id, the #808 backfill left some without it), so
 * it is never a hand-logged row and is never claimed. squarePaymentRecorded
 * already finds the payment such a row records through its reference.
 */
const SQUARE_WRITTEN_NOTE_PREFIXES = ["Reconciled from Square", "Square payment (Order"] as const;

export function isSquareWrittenNote(notes: string | null | undefined): boolean {
  return !!notes && SQUARE_WRITTEN_NOTE_PREFIXES.some((prefix) => notes.startsWith(prefix));
}

/** A quote_payments row with no Square id that could be this Square payment. */
interface HandLoggedPayment {
  id: string;
  /** PostgREST returns `numeric` columns as numbers or strings. */
  amount: number | string;
  fee_amount: number | string | null;
  payment_date: string | null;
  notes?: string | null;
}

interface QueryError {
  message: string;
  code?: string;
}

interface QueryResult {
  data: Record<string, unknown>[] | null;
  error: QueryError | null;
}

/** The slice of a PostgREST filter chain this module uses. */
export interface PaymentsQuery extends PromiseLike<QueryResult> {
  eq(column: string, value: string): PaymentsQuery;
  is(column: string, value: null): PaymentsQuery;
  in(column: string, values: readonly string[]): PaymentsQuery;
  limit(count: number): PaymentsQuery;
  select(columns: string): PaymentsQuery;
}

/** The row both callers insert when nothing is there to claim. */
export interface SquarePaymentRow {
  quote_id: string;
  amount: number;
  fee_amount: number;
  method: "square";
  reference: string;
  square_payment_id: string;
  notes: string;
  payment_date: string;
}

/** The slice of the service-role Supabase client this module reads and writes through. */
export interface PaymentsDb {
  from(table: "quote_payments"): {
    select(columns: string): PaymentsQuery;
    update(values: { square_payment_id: string; fee_amount: number }): PaymentsQuery;
    insert(values: SquarePaymentRow): PromiseLike<{ error: QueryError | null }>;
  };
}

/** Days since the epoch for a `YYYY-MM-DD` (or ISO timestamp) date, or null. */
function dayNumber(date: string | null): number | null {
  const m = date ? /^(\d{4})-(\d{2})-(\d{2})/.exec(date) : null;
  if (!m) return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86_400_000;
}

/**
 * The day a Square payment is matched and recorded on: the UTC day of its
 * created_at, else today (#1243). square-reconcile uses the same day, so both
 * writers search the same claim window for the same payment.
 */
export function squarePaymentDate(createdAt: string | null | undefined, now: Date = new Date()): string {
  return createdAt && dayNumber(createdAt) !== null ? createdAt.slice(0, 10) : now.toISOString().slice(0, 10);
}

/**
 * The rows that could be this payment, best first: the same amount to the cent,
 * payment_date within CLAIM_WINDOW_DAYS, closest date first (then earliest, then
 * id, so two writers rank the same rows the same way).
 */
export function rankHandLoggedMatches(
  rows: HandLoggedPayment[],
  amountCents: number,
  paymentDate: string,
): HandLoggedPayment[] {
  const target = dayNumber(paymentDate);
  if (target === null) return [];
  return rows
    .map((row) => ({ row, days: dayNumber(row.payment_date) }))
    .filter((c): c is { row: HandLoggedPayment; days: number } =>
      c.days !== null &&
      Math.abs(c.days - target) <= CLAIM_WINDOW_DAYS &&
      Math.round(Number(c.row.amount) * 100) === amountCents)
    .sort((a, b) =>
      Math.abs(a.days - target) - Math.abs(b.days - target) ||
      a.days - b.days ||
      a.row.id.localeCompare(b.row.id))
    .map((c) => c.row);
}

/**
 * True when this Square payment is already on the quote: by square_payment_id
 * (which a claimed row carries), or by reference for rows written before that
 * column existed (#801). Throws on a failed lookup, so a caller never reads an
 * error as "not recorded".
 */
export async function squarePaymentRecorded(
  db: PaymentsDb,
  quoteId: string,
  squarePaymentId: string,
): Promise<boolean> {
  for (const column of ["square_payment_id", "reference"]) {
    const { data, error } = await db
      .from("quote_payments")
      .select("id")
      .eq("quote_id", quoteId)
      .eq(column, squarePaymentId)
      .limit(1);
    if (error) throw new Error(`payment lookup failed for quote ${quoteId}: ${error.message}`);
    if ((data?.length ?? 0) > 0) return true;
  }
  return false;
}

export interface SquarePaymentInput {
  quoteId: string;
  squarePaymentId: string;
  /** What the quote is credited, fee excluded (quoteCreditCents). */
  amountCents: number;
  feeCents: number;
  /**
   * `YYYY-MM-DD`: the date matched against, and the date inserted. Callers
   * derive it from the payment with squarePaymentDate.
   */
  paymentDate: string;
  notes: string;
  /**
   * Claim a matching hand-logged row, but never insert one (#1219): set for a
   * payment found only through its note and older than
   * NOTE_MATCH_INSERT_WITHIN_DAYS.
   */
  claimOnly?: boolean;
}

export type RecordOutcome =
  | { outcome: "inserted" }
  | {
      outcome: "claimed";
      claimedId: string;
      /** How many rows matched. More than one is reported through `warn`. */
      candidates: number;
      /** The claimed row's fee_amount before it was overwritten, for the log (undo). */
      previousFeeAmount: number | null;
    }
  /** Another writer already put this payment on the quote (unique index). */
  | { outcome: "exists" }
  /** `claimOnly` was set and no hand-logged row matched: nothing was written. */
  | { outcome: "unclaimed" };

/** Where a claim that had to choose between several matching rows is reported. */
export type ClaimWarn = (message: string, meta: Record<string, unknown>) => void;

/**
 * Put a Square payment on a quote the caller has already checked with
 * squarePaymentRecorded: claim the best-matching hand-logged card row (its
 * Square id and fee are set, its amount is left alone), else insert a new row
 * (or, with `claimOnly`, write nothing).
 * When several rows matched, the claim is reported through `warn`.
 * A claim only lands while the row's id is still NULL, so when another writer
 * claims it first this moves on to the next match. The (quote_id,
 * square_payment_id) unique index turns a writer racing the same payment into
 * "exists". Any other failed read or write throws, so the caller skips
 * (reconcile) or retries (webhook) rather than inserting a duplicate.
 * Rows Square sync wrote (isSquareWrittenNote) are never claimed (#1230).
 */
export async function recordOrClaimSquarePayment(
  db: PaymentsDb,
  input: SquarePaymentInput,
  warn: ClaimWarn,
): Promise<RecordOutcome> {
  const { data, error } = await db
    .from("quote_payments")
    .select("id, amount, fee_amount, payment_date, notes")
    .eq("quote_id", input.quoteId)
    .is("square_payment_id", null)
    .in("method", HAND_LOGGED_CARD_METHODS);
  if (error) throw new Error(`hand-logged payment lookup failed for quote ${input.quoteId}: ${error.message}`);

  const handLogged = ((data ?? []) as unknown as HandLoggedPayment[]).filter((row) => !isSquareWrittenNote(row.notes));
  const ranked = rankHandLoggedMatches(handLogged, input.amountCents, input.paymentDate);
  for (const row of ranked) {
    const previousFeeAmount = row.fee_amount == null ? null : Number(row.fee_amount);
    const { data: updated, error: updateErr } = await db
      .from("quote_payments")
      .update({ square_payment_id: input.squarePaymentId, fee_amount: input.feeCents / 100 })
      .eq("id", row.id)
      .is("square_payment_id", null)
      .select("id");
    if (updateErr) {
      if (updateErr.code === "23505") return { outcome: "exists" };
      throw new Error(`claiming payment ${row.id} failed: ${updateErr.message}`);
    }
    if ((updated?.length ?? 0) > 0) {
      if (ranked.length > 1) {
        warn("several hand-logged payments match — claimed the closest date", {
          quote_id: input.quoteId,
          payment_id: input.squarePaymentId,
          row_id: row.id,
          candidates: ranked.length,
        });
      }
      return { outcome: "claimed", claimedId: row.id, candidates: ranked.length, previousFeeAmount };
    }
  }

  if (input.claimOnly) return { outcome: "unclaimed" };

  const { error: insertErr } = await db.from("quote_payments").insert({
    quote_id: input.quoteId,
    amount: input.amountCents / 100,
    fee_amount: input.feeCents / 100,
    method: "square",
    reference: input.squarePaymentId,
    square_payment_id: input.squarePaymentId,
    notes: input.notes,
    payment_date: input.paymentDate,
  });
  if (insertErr) {
    if (insertErr.code === "23505") return { outcome: "exists" };
    throw new Error(`payment insert failed for quote ${input.quoteId}: ${insertErr.message}`);
  }
  return { outcome: "inserted" };
}
